<template>
  <div class="page-table scrollable">
    <div class="table-box">
      <el-table
        :height="tableHeight"
        :data="collection"
        ref="cTable"
        highlight-current-row
        @current-change="handleCurrentChange"
        @sort-change="sortChange"
        style="width: 100%"
      >
        <el-table-column
          v-for="(col, idx) in columns"
          :key="idx"
          :type="col.datatype"
          :prop="col.name"
          :label="col.label ? col.label : col.name"
          :width="col.width"
          :min-width="col['min-width']"
          :row-key="currentRow"
          :sortable="col.sortable ? 'custom' : false"
          class-name="subtitle2"
        >
          <template slot-scope="scope">
            <div v-if="col.datatype === 'boolean'">
              <el-tag size="small">{{ scope.row[col.name] ? 'Active' : 'Inactive' }}</el-tag>
            </div>

            <div v-else-if="col.datatype === 'datetime'">
              <time class="body1">{{ getFormatedDateTime(scope.row, col) }}</time>
            </div>

            <div v-else-if="col.datatype === 'objectpath' && !col.display">
              <span class="body1">{{ getObjectPathValue(scope.row, col.name) }}</span>
            </div>

            <div v-else-if="col.display === 'icon'">
              <span class="body1"> <i :class="getIcon(scope.row, col, col.displayKey).icon"></i> </span>
            </div>

            <div v-else-if="col.display === 'iconAndText'">
              <span class="body1">
                <i
                  class="display-inline-block"
                  :style="[
                    { color: getColorTagValue(scope.row, col, col.displayKey).color },
                    { background: getColorTagValue(scope.row, col, col.displayKey).color }
                  ]"
                  :class="getIcon(scope.row, col, col.displayKey).icon"
                ></i>
                {{ getColorTagValue(scope.row, col, col.displayKey).display }}
              </span>
            </div>

            <div v-else-if="col.display === 'colorTag'">
              <el-tag
                class="button2 capitalize-first-letter"
                size="small"
                :style="[
                  { color: getColorTagValue(scope.row, col, col.displayKey).textColor },
                  { 'border-color': getColorTagValue(scope.row, col, col.displayKey).textColor }
                ]"
                :color="getColorTagValue(scope.row, col, col.displayKey).color"
                >{{ getColorTagValue(scope.row, col, col.displayKey).display }}</el-tag
              >
            </div>

            <div v-else-if="col.display === 'stateTag'">
              <state-tag :status="scope.row[col.name]" :class-list="'button2 capitalize-first-letter'"/>
            </div>

            <div v-else-if="col.display === 'link'">
              <router-link v-if="col.query" :to="{ name: col.pathTo, query: getQueryParamObject(scope.row, col) }">
                <a v-if="col.linkLabel">{{ getObjectPathValue(scope.row, col.linkLabel.labelName) }}</a>
                <a v-else>
                  {{ col.datatype === 'objectpath' ? getObjectPathValue(scope.row, col.name) : scope.row[col.name] }}
                </a>
              </router-link>
              <router-link v-else :to="{ name: col.pathTo, params: getQueryParamObject(scope.row, col, 'params') }">
                <a v-if="col.linkLabel">{{ getObjectPathValue(scope.row, col.linkLabel.labelName) }}</a>
                <a v-else>
                  {{ col.datatype === 'objectpath' ? getObjectPathValue(scope.row, col.name) : scope.row[col.name] }}
                </a>
              </router-link>
            </div>

            <div v-else-if="col.display === 'mitrelink'">
              <span class="value">
                <a :href="getObjectPathValue(scope.row, col.link)" target="_blank" rel="noreferrer">
                  {{ getObjectPathValue(scope.row, col.linkLabel.labelId) }}:
                  {{ getObjectPathValue(scope.row, col.linkLabel.labelName) }}
                </a>
              </span>
            </div>

            <div v-else-if="col.display === 'jiraLink'">
              <span class="value">
                <a
                  v-if="getObjectPathValue(scope.row, col.link)"
                  :href="getObjectPathValue(scope.row, col.link)"
                  target="_blank"
                  rel="noreferrer"
                >
                  {{ getObjectPathValue(scope.row, col.name) }}
                </a>
                <span v-else>
                  {{ getObjectPathValue(scope.row, col.name) }}
                </span>
              </span>
            </div>

            <div v-else-if="col.display === 'popup'">
              <el-link @click="emitCallbackEvent('popup-click', scope.row, col.name)">{{
                col.datatype === 'objectpath' ? getObjectPathValue(scope.row, col.name) : scope.row[col.name]
              }}</el-link>
            </div>

            <div v-else-if="col.display === 'riskscore'">
              <span class="body1">
                <i class="color-block" :class="`${scope.row.riskLevel.level}-risk-color`"></i>
                <span class="body1">{{ scope.row[col.name] }}</span>
              </span>
            </div>

            <div v-else-if="col.display === 'switch'">
              <span class="body1">
                <el-switch
                  v-model="scope.row[col.name]"
                  :width="40"
                  :active-value="col.displayKey.active.key"
                  :inactive-value="col.displayKey.inactive.key"
                  :active-text="
                    getObjectPathValue(scope.row, col.name) === col.displayKey.active.key
                      ? col.displayKey.active.display
                      : col.displayKey.inactive.display
                  "
                  :disabled="true"
                >
                </el-switch>
              </span>
            </div>

            <div v-else-if="col.display === 'component'">
              <span v-if="col.component === 'CyberKillChainTickChart'">
                <cyber-kill-chain-tick-chart
                  :chart-id="`${scope.$index}`"
                  :chart-data="getObjectPathValue(scope.row, col.name)"
                  :key="`${scope.$index}_${getObjectPathValue(scope.row, col.componentKey)}`"
                />
                <a :href="getObjectPathValue(scope.row, col.link)" target="_blank" rel="noreferrer" v-if="col.link">
                  <span v-if="col.displayTextName">{{ getObjectPathValue(scope.row, col.displayTextName) }}</span>
                </a>
                <span v-else-if="col.displayTextName">{{ getObjectPathValue(scope.row, col.displayTextName) }}</span>
              </span>
              <span v-if="col.component === 'RecommendationProgress'">
                <recommendation-progress :row-data="scope.row" />
              </span>
              <span v-if="col.component === 'PercentComplete'">
                <percent-complete
                  :key="scope.row.key"
                  :completed="getObjectPathValue(scope.row, col.componentFields.completed)"
                  :total="getObjectPathValue(scope.row, col.componentFields.total)"
                />
              </span>
              <span v-if="col.component === 'UserButton'">
                <user-button
                  :classList="getObjectPathValue(scope.row, col.name.split('.')[0]).firstname ? 'shamrock-200' : ''"
                  :user="getObjectPathValue(scope.row, col.name.split('.')[0])"
                  :key="`${scope.$index}_${getObjectPathValue(scope.row, col.componentKey)}`"
                />
                {{ getObjectPathValue(scope.row, col.name) }}
              </span>
              <span v-if="col.component === 'IntegrationStatusTableCell'">
                <integration-status-table-cell
                  :status="getObjectPathValue(scope.row, col.componentFields.status)"
                  :assetCount="getObjectPathValue(scope.row, col.componentFields.assetCount)"
                  :assetText="getObjectPathValue(scope.row, col.componentFields.assetText)"
                  :lastScanTime="getObjectPathValue(scope.row, col.componentFields.lastScanTime)"
                  :assetLink="col.componentFields.assetLink"
                  :accountType="getObjectPathValue(scope.row, col.componentFields.accountType)"
                  :accountKey="getObjectPathValue(scope.row, col.componentFields.accountKey)"
                  :nickName="getObjectPathValue(scope.row, col.componentFields.nickName)"
                  :source="getObjectPathValue(scope.row, col.componentFields.source)"
                />
              </span>
              <span v-if="col.component === 'AssetCountTableCell'">
                <asset-count-table-cell
                  :vmCount="getObjectPathValue(scope.row, col.componentFields.vmCount)"
                  :containerCount="getObjectPathValue(scope.row, col.componentFields.containerCount)"
                  :accountKey="getObjectPathValue(scope.row, col.componentFields.accountKey)"
                  :nickName="getObjectPathValue(scope.row, col.componentFields.nickName)"
                />
              </span>
              <span v-if="col.component === 'SensorCoverageTableCell'">
                <sensor-coverage-table-cell
                  :completed="getObjectPathValue(scope.row, col.componentFields.completed)"
                  :total="getObjectPathValue(scope.row, col.componentFields.total)"
                />
              </span>
              <span v-if="col.component === 'DiscoverySourceTableCell'">
                <discovery-source-table-cell
                  :icon="getIcon(scope.row, col, col.displayKey).icon"
                  :discoveryKey="getObjectPathValue(scope.row, col.componentFields.discoveryKey)"
                />
              </span>
            </div>

            <div v-else-if="col.display === 'tag'">
              <el-tag size="medium" v-for="(item, idx) in scope.row[col.name]" :key="idx">{{ item }}</el-tag>
            </div>

            <span v-else-if="col.display === 'fingerprintLabel'" class="body1">
              {{ scope.row[col.display] }}
              <span
                v-if="scope.row[col.name]"
                class="display-inline-block icon-copy-to-clipboard cursor-pointer"
                @click="copyToClipboard(scope.row, col.name)"
              >
              </span>
            </span>

            <div v-else-if="col.datatype === 'array' || col.display === 'array'">
              <span v-for="(item, idx) in getObjectPathValue(scope.row, col.name)" :key="idx">
                {{ item }}
                <el-divider
                  v-if="idx !== getObjectPathValue(scope.row, col.name).length - 1"
                  direction="vertical"
                ></el-divider>
              </span>
            </div>

            <div v-else>
              <span class="body1">{{ scope.row[col.name] }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          v-if="actions && actions.length > 0"
          fixed="right"
          label="Action"
          width="120"
          class-name="table-action subtitle2"
        >
          <template slot-scope="scope">
            <span v-for="action in actions" :key="action.name"
              :class="{'hide-action': action.hide && action.hide.values.includes(scope.row[action.hide.name])}">
              <template v-if="$allow(action.rbac.subject, action.rbac.operation)">
                <el-tooltip
                  class="item"
                  effect="dark"
                  :content="
                    action.tooltipAlternate
                      ? scope.row['active']
                        ? action.tooltip
                        : action.tooltipAlternate
                      : action.tooltip
                  "
                  placement="top"
                  :enterable="false"
                >
                  <span
                    type="text"
                    class="text-button mr-16"
                    v-if="$_.has(action, 'showActionName') && action.showActionName"
                    @click.prevent.stop="action.actionHandler(scope)"
                  >
                    {{ action.name }}
                  </span>
                  <el-button type="text" size="medium" @click.prevent.stop="action.actionHandler(scope)" v-else>
                    <i
                      :class="[
                        action.icon,
                        {
                          'fa-pause-circle':
                            action.name === 'pause' &&
                            ((scope.row.state && scope.row.state === 'ENABLED') || scope.row.enabled),
                          'fa-play-circle':
                            action.name === 'pause' &&
                            !((scope.row.state && scope.row.state === 'ENABLED') || scope.row.enabled),
                        }
                      ]"
                    >
                      {{
                        action.iconAlternate
                          ? scope.row['active']
                            ? action.iconSubject
                            : action.iconAlternate
                          : action.iconSubject
                      }}
                    </i>
                  </el-button>
                </el-tooltip>
              </template>
            </span>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <el-pagination
      background
      layout="prev, pager, next"
      :page-size="this.tableData.recordsPerPage"
      :total="this.tableData.totalRecords"
      :current-page="this.tableData.currentPage"
      prev-text="PREV"
      next-text="NEXT"
      hide-on-single-page
      style="text-align: center; padding-top: 20px"
      @current-change="changePage"
    ></el-pagination>
  </div>
</template>

<script>
import BasicTableElement from '@/components/table/BasicTableElement.vue'
import StateTag from "../button/StateTag.vue";
export default {
  name: 'ActionTableElement',
  components: {StateTag},
  extends: BasicTableElement,
  props: {
    columns: { type: Array },
    tableData: { type: Object },
    actions: { type: Array }
  },
  data () {
    return {
      currentRow: undefined,
      collection: []
    }
  },
  methods: {},
  mounted () {
    this.tableHeight = `${this.$el.parentElement.clientHeight - this.heightOffset - 60}`
  }
}
</script>

<style scoped>
@import 'assets/styles/_variables.css';
@import 'assets/styles/colors.css';
@import 'assets/styles/tables.css';

.hide-action {
  visibility: hidden;
}
</style>



// WEBPACK FOOTER //
// src/components/table/ActionTableElement.vue